/**
 * GlobalTemplates — network-wide message templates (study_id NULL) a recruiter
 * can use on ANY contact regardless of study. Admin-managed (the API gates
 * global creation to CRM admins); they surface in the composer's Templates
 * dropdown alongside the contact's study templates.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Globe } from 'lucide-react';
import api from '../../services/api';
import { MessageTemplate } from '../types';
import { useIsCRMAdmin } from '../lib';
import { useConfirm } from '../components/ui';
import PageHeader from '../components/PageHeader';
import TemplateForm, { ChannelChip, CategoryChip, TemplateDraft } from '../components/studies/TemplateForm';

export default function GlobalTemplates() {
  const isAdmin = useIsCRMAdmin();
  const confirm = useConfirm();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/crm/templates/global');
      setTemplates(res.data?.templates || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (err: any) {
      setError(err?.response?.data?.error || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const create = (v: TemplateDraft) => run('Save', async () => {
    await api.post('/api/crm/templates/global', v);
    setAdding(false);
    await load();
  });
  const update = (id: string, v: TemplateDraft) => run('Save', async () => {
    await api.patch(`/api/crm/templates/${id}`, v);
    setEditingId(null);
    await load();
  });
  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'Remove this global template?',
      confirmLabel: 'Remove',
      danger: true,
    }))) return;
    run('Remove', async () => { await api.delete(`/api/crm/templates/${id}`); await load(); });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader
        icon={Globe}
        title="Global templates"
        subtitle="Network-wide quick replies recruiters can use on any contact — they appear in the composer alongside each study's templates."
        actions={isAdmin && !adding && (
          <button
            type="button"
            onClick={() => { setAdding(true); setEditingId(null); }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <Plus className="h-4 w-4" /> Add template
          </button>
        )}
      />

      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        {adding && (
          <div className="mb-3">
            <TemplateForm busy={busy === 'Save'} onSave={create} onCancel={() => setAdding(false)} />
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : templates.length === 0 && !adding ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No global templates yet.{isAdmin ? ' Add one with “Add template”.' : ' An admin can add them.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {templates.map((t) => (
              <li key={t.id} className="py-3">
                {editingId === t.id ? (
                  <TemplateForm
                    initial={{ channel: t.channel, category: t.category, title: t.title, subject: t.subject || '', body: t.body }}
                    busy={busy === 'Save'}
                    onSave={(v) => update(t.id, v)}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</span>
                        <ChannelChip channel={t.channel} />
                        <CategoryChip category={t.category} />
                      </div>
                      {t.subject && <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300">Subject: {t.subject}</p>}
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-400">{t.body}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex shrink-0 items-center gap-1">
                        <button type="button" onClick={() => { setEditingId(t.id); setAdding(false); }} aria-label="Edit template"
                          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => remove(t.id)} aria-label="Remove template"
                          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded p-1.5 text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 sm:min-h-0 sm:min-w-0">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
